import { useEffect, useState } from 'react';
import { Archive } from 'lucide-react';
import type { PaigeRetention, PaigeWorkbenchBridge } from './paige.global';

/** The four choices as the setup map shows them, in the contract's order. */
const choices: readonly { value: PaigeRetention; label: string }[] = [
  { value: 'export_7d', label: '7 days' },
  { value: 'export_30d', label: '30 days' },
  { value: 'export_1y', label: '1 year' },
  { value: 'forever', label: 'Forever' },
];

const explanation =
  'Unexported documents are always kept. A page kept more than 90 days uses 1 page a month.';

/**
 * The job's retention in the setup map's "How it works" column, next to the email card: how long
 * Paige keeps a document's pages after it was exported, as a segmented control that saves through
 * the job's settings. Renders nothing when the workbench is not on a Paige job.
 */
export function RetentionCard() {
  const { value, saving, error, choose } = useRetention(window.paigeWorkbench);
  if (value === null) return null;
  return (
    <div className="setup-map-setting paige-retention">
      <Archive size={22} aria-hidden="true" />
      <span>
        <strong>Keep documents</strong>
        <small>{detailLine(value)}</small>
        <span className="paige-retention-choices" role="radiogroup" aria-label="Keep documents">
          {choices.map((choice) => (
            <button
              type="button"
              key={choice.value}
              role="radio"
              aria-checked={choice.value === value}
              className={`paige-retention-choice${choice.value === value ? ' selected' : ''}`}
              disabled={saving}
              onClick={() => void choose(choice.value)}
            >
              {choice.label}
            </button>
          ))}
        </span>
        <small>{error ?? explanation}</small>
      </span>
    </div>
  );
}

/** The retention from Paige's bridge, and a choose that saves it and rolls back on refusal. */
function useRetention(bridge: PaigeWorkbenchBridge | undefined) {
  const [value, setValue] = useState<PaigeRetention | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!bridge?.retention || !bridge.setRetention) return;
    let live = true;
    bridge
      .retention()
      .then((current) => live && setValue(current))
      .catch(() => live && setValue(null));
    return () => {
      live = false;
    };
  }, [bridge]);
  const choose = async (next: PaigeRetention) => {
    if (next === value || saving || !bridge?.setRetention) return;
    const previous = value;
    setValue(next);
    setSaving(true);
    setError(null);
    try {
      await bridge.setRetention(next);
    } catch (failure) {
      setValue(previous);
      setError(failure instanceof Error ? failure.message : 'Paige could not save the change');
    } finally {
      setSaving(false);
    }
  };
  return { value, saving, error, choose };
}

function detailLine(value: PaigeRetention): string {
  if (value === 'forever') return 'Exported documents stay until you delete the job';
  const label = choices.find((c) => c.value === value)?.label ?? '';
  return `Exported documents are removed ${label} after export`;
}
